import { State } from '@autopylot-internal/state';
import { DevSettings } from '../../support/DevSettings.ts';
import { IFreeTierLimits, IOfferMeta } from './IOfferMeta.ts';
import { UserFacingError } from '../../support/UserFacingError.ts';
import { AutoPylotPrincipal } from '../../auth/AutoPylotPrincipal.ts';
import { Logger } from '../../support/debug/Logger.ts';
import { Device } from '../../support/Device.ts';
import { RevenueCatSDKModel } from './RevenueCatSDKModel.ts';
import { RevenueCatAPIModel } from './RevenueCatAPIModel.ts';
import { eventually } from '../../support/helpers/eventually.ts';
import { App, AppState } from '@capacitor/app';

const FOUR_MINUTES_IN_MS = 1000 * 60 * 4;

export const ENTITLEMENTS = {
    PLUS: 'e_plus'
};

const sensibleDefaults: IFreeTierLimits = {
    windyPreviewsPerMonth: 3
};

type IPaymentMode = 'real' | 'bypass' | 'none';

export type IPaymentTerm = 'monthly' | 'annual';

export class RevenueCatModel extends State<Record<string, never>> {
    static inject = () => [
        RevenueCatAPIModel,
        RevenueCatSDKModel,
        AutoPylotPrincipal,
        DevSettings,
        Logger,
        Device
    ];
    constructor(
        private api: RevenueCatAPIModel,
        private sdk: RevenueCatSDKModel,
        private principal: AutoPylotPrincipal,
        private devSettings: DevSettings,
        private logger: Logger,
        private deviceInfo: Device,
    ){
        super({ });
        this.api.subscribe(() => {
            this.setState({});
        });
        this.sdk.subscribe(() => {
            this.setState({});
        });
    }

    private failedToInitialize = false;

    isDown = () => {
        return this.failedToInitialize;
    };

    getPaymentMode = (): IPaymentMode => {
        const canBypass = this.api.canBypassPayments();
        const wantsBypass = this.devSettings.bypassPayments;
        const canReal = this.sdk.canMakeRealPayment();

        if (canBypass && wantsBypass) {
            return 'bypass';
        }

        if (canReal){
            return 'real';
        }

        return 'none';
    };

    hasUpgraded = () => {
        if (this.isDown()) {
            // if RevenueCat is down, temporarily give everyone AP+
            return true;
        }
        return this.api.hasUpgraded();
    };

    isEligibleForTrial = () => {
        if (this.devSettings.eligibleForTrial) {
            return true;
        } else if (this.getPaymentMode() === 'real') {
            return this.sdk.isEligibleForTrial();
        } else {
            // if dev setting not set, and not real, assume no trial
            return false;
        }
    };

    getOfferMeta = (): IOfferMeta => {
        return this.api.getOfferMeta();
    };

    getTrialDays = (): number => {
        const platform = this.deviceInfo.platform;
        const { trialDaysApple = 7, trialDaysGoogle = 7, trialDaysWeb = 7 } = this.getOfferMeta().paywallInfo || {};
        return (platform === 'ios' ? trialDaysApple : platform === 'android' ? trialDaysGoogle : trialDaysWeb) || 7;
    };

    getFreeTierLimits = () : IFreeTierLimits => {
        if (this.hasUpgraded()){
            throw new Error('the user has upgraded and has no limits');
        }

        let limits: IFreeTierLimits | undefined = undefined;

        if (this.getPaymentMode() === 'real'){
            limits = this.sdk.getFreeTierLimits();
        } else {
            limits = this.api.getOfferMeta().freeTierLimits;
        }

        if (!limits){
            this.logger.error(`WARN: RevenueCat is using "sensible defaults" b/c user ${this.principal.userId} was not able to get limits from their offer`);
            limits = sensibleDefaults;
        }

        return limits;
    };

    init = async () => {
        this.logger.info('RevenueCatModel.init-ing');
        await this._initialize();
        this._refreshOnForeground();
        this.logger.info('RevenueCatModel.init-ed', this.getDiagnostics().diagnostics);
    };

    private _initialize = async () => {
        try {
            await this.api.init();

            if (this.api.isDown()) {
                this.logger.error('RevenueCat api is down');
                this.failedToInitialize = true;
                return;
            }

            await this.sdk.init();
            this.failedToInitialize = false;
        } catch {
            this.failedToInitialize = true;
        }
    };

    private _refreshOnForeground = () => {
        App.addListener('appStateChange', ({ isActive: isForegrounding}: AppState) => {
            if (isForegrounding){
                this._initialize().catch((err: any) => {
                    this.logger.error(`WARN failed to reinitialize while foregrounding. ${err.toString()}`);
                });

                setTimeout(() => {
                    this._initialize().catch((err: any) => {
                        this.logger.error(`WARN failed to reinitialize while foregrounding. ${err.toString()}`);
                    });
                }, FOUR_MINUTES_IN_MS);
            }
        });
    };

    // the app always shows the purchase page and throws an error if they cannot
    // kept this around for a hint in the debug page for DX
    canUpgrade = () => {
        if (this.hasUpgraded()){
            return false;
        }
        return this.getPaymentMode() !== 'none';
    };

    private guardAgainstCannotUpgrade = () => {
        if (this.getPaymentMode() === 'bypass'){
            return;
        }

        if (!this.canUpgrade()){
            throw new Error('upgrade was called when .canUpgrade() returns false');
        }
    };

    upgrade = async (term: IPaymentTerm = 'monthly') => {
        this.guardAgainstCannotUpgrade();

        const paymentMode = this.getPaymentMode();
        if (paymentMode === 'real'){
            await this.handleSDKUpgrade(term);
        }
        else if (paymentMode === 'bypass'){
            await this.api.upgrade();
        }
        else {
            throw new Error(`mode ${paymentMode} is not supported`);
        }

        await eventually(async () => {
            if (!this.hasUpgraded()) {
                await new Promise(res => setTimeout(res, 1000));
                await this.api.fetchEntitlements();
            }
            if (!this.hasUpgraded()) {
                throw new UserFacingError({
                    message: `user ${this.principal.userId} just completed an upgrade in mode ${this.getPaymentMode()} but does not appear to be upgraded`,
                    displayHeader: 'Sorry, your upgrade is taking longer than usual',
                    displayMessage: `Wait a few minutes, then try restarting the app. If you're still not seeing AutoPylot+ features, please let us know.`,
                    errorCode: 'slow-upgrade',
                    status: 500
                });
            }
        }, 10000, 250);
    };

    private handleSDKUpgrade = async (term: 'monthly' | 'annual') => {
        await this.sdk.upgrade(term);
        await this.api.fetchEntitlements();
    };

    canRestore = () => {
        if (this.hasUpgraded()){
            return false;
        }
        if (this.deviceInfo.platform === 'web') {
            return false;
        }
        if (this.getPaymentMode() === 'none') {
            return false;
        }
        return true;
    };

    restore = async () => {
        if (!this.canRestore()){
            throw new Error('restore was called when .canRestore() returns false');
        }

        if (this.getPaymentMode() === 'bypass') {
            throw new UserFacingError({
                status: 400,
                message: 'restore not supported',
                errorCode: 'restore-not-supported',
                displayHeader: 'Restore not supported',
                displayMessage: 'Cannot restore in bypass mode'
            });
        }

        await this.sdk.restore();
        await this.api.fetchEntitlements();

        if (!this.hasUpgraded()) {
            throw new UserFacingError({
                status: 400,
                message: 'nothing to restore',
                errorCode: 'no-entitlements-to-restore',
                displayHeader: 'No Prior Purchases',
                displayMessage: 'Sorry, we did not find any prior purchases to restore.'
            });
        }
    };

    canManageSubscription = () => {
        if (!this.hasUpgraded()){
            return false;
        }
        if (this.api.hasSelfLink()){
            return true;
        }
        if (this.sdk.getManagementLink()){
            return true;
        }
        return false;
    };

    /**
     * For internal users that are bypassing payment. This will downgrade.
     * For ios/android users, this will launch into store subscriptions, where they might cancel.
     * If they do, it won't be effective for 3 minutes.
     */
    manageSubscription = async () => {
        const managementLink = this.sdk.getManagementLink();
        if (this.api.hasSelfLink()){
            await this.api.downgradeWithSelfLink();
        }
        else if (managementLink){
            window.open(managementLink);
        }
        else {
            throw new Error('manageSubscription was called without a self link or a management link, maybe .canManageSubscription() was not called first');
        }
    };

    getDiagnostics = () => {
        const diagnostics: { [label: string]: any } = {
            userId: this.principal.userId,
            canUpgrade: this.canUpgrade(),
            hasUpgraded: this.hasUpgraded(),
            paymentMode: this.getPaymentMode(),
            deviceInfo: this.deviceInfo,
            freeTierLimits: !this.hasUpgraded() ? this.getFreeTierLimits() : undefined,
            offerMeta: !this.hasUpgraded() ? this.getOfferMeta() : undefined,
            canManageSubscription: this.canManageSubscription()
        };

        return {
            diagnostics,
        };
    };
}
